/**
 * BlockControls — wrapper d'admin pour un encart de page publique.
 *
 * Affiche des contrôles au survol quand `useAuth().user` est présent :
 *   - Œil : masquer/afficher (persistant via CMS)
 *   - ↑ ↓ : déplacer l'encart dans la liste des encarts de la section
 *   - 🗑 : supprimer définitivement
 *
 * L'état "masqué" est stocké dans `content.blocks.<path>.hidden = true`.
 * L'ordre est stocké dans `content.blocks.<sectionPath>.order = ["id1","id2",...]`.
 * Le composant NE persiste PAS lui-même ; il appelle les callbacks fournis.
 *
 * Usage :
 *   <BlockControls
 *     blockId="essential"
 *     onHide={() => saveText(`experiences.${x.id}.blocks.essential.hidden`, true)}
 *     onDelete={() => saveText(`experiences.${x.id}.blocks.essential.deleted`, true)}
 *     onMoveUp={() => moveBlock(-1)}
 *     onMoveDown={() => moveBlock(+1)}
 *   >
 *     <div>...contenu de l'encart...</div>
 *   </BlockControls>
 *
 * Un encart masqué reste visible pour l'admin (grisé + badge "masqué") et
 * complètement caché pour le public.
 */
import { useState } from "react";
import { Eye, EyeOff, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function BlockControls({
  blockId,
  hidden = false,
  deleted = false,
  canMoveUp = true,
  canMoveDown = true,
  onHide,
  onShow,
  onDelete,
  onMoveUp,
  onMoveDown,
  children,
  as: Tag = "div",
  className = "",
}) {
  const { user } = useAuth();
  const isAdmin = !!user;
  const [busy, setBusy] = useState(false);

  // Public : masqué ou supprimé → on ne rend rien
  if (!isAdmin && (hidden || deleted)) return null;
  if (deleted && !isAdmin) return null;

  // Public : rendu normal sans contrôles
  if (!isAdmin) return <Tag className={className}>{children}</Tag>;

  const run = async (fn) => {
    if (!fn || busy) return;
    setBusy(true);
    try { await fn(); } catch (_) {} finally { setBusy(false); }
  };

  return (
    <Tag
      data-testid={`block-${blockId}`}
      className={`${className} group/block relative`}
      style={{
        outline: hidden || deleted ? "2px dashed rgba(139,37,48,0.35)" : "none",
        outlineOffset: 8,
        borderRadius: 8,
        opacity: hidden || deleted ? 0.55 : 1,
        transition: "opacity 200ms",
      }}
    >
      {(hidden || deleted) && (
        <div
          className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full bg-refuge-ruby text-white text-[10px] tracking-[0.2em] uppercase font-body"
          data-testid={`block-${blockId}-hidden-badge`}
        >
          {deleted ? "Supprimé" : "Masqué"} — visible admin uniquement
        </div>
      )}
      <div className="absolute -top-3 right-2 z-30 flex items-center gap-1 opacity-0 group-hover/block:opacity-100 transition-opacity">
        {hidden || deleted ? (
          <button
            type="button"
            onClick={() => run(onShow)}
            disabled={busy}
            data-testid={`block-${blockId}-show`}
            title="Ré-afficher"
            className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 disabled:opacity-40 shadow-md"
          >
            <Eye size={12} />
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => run(onMoveUp)}
              disabled={busy || !canMoveUp}
              data-testid={`block-${blockId}-up`}
              title="Monter"
              className="w-7 h-7 rounded-full bg-refuge-text/85 text-white flex items-center justify-center hover:bg-refuge-text disabled:opacity-30 shadow-md"
            >
              <ArrowUp size={12} />
            </button>
            <button
              type="button"
              onClick={() => run(onMoveDown)}
              disabled={busy || !canMoveDown}
              data-testid={`block-${blockId}-down`}
              title="Descendre"
              className="w-7 h-7 rounded-full bg-refuge-text/85 text-white flex items-center justify-center hover:bg-refuge-text disabled:opacity-30 shadow-md"
            >
              <ArrowDown size={12} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Masquer cet encart ? Il ne sera plus visible côté public. Tu pourras le réafficher depuis cette même page (icône œil verte).")) {
                  run(onHide);
                }
              }}
              disabled={busy}
              data-testid={`block-${blockId}-hide`}
              title="Masquer"
              className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center hover:bg-amber-600 disabled:opacity-40 shadow-md"
            >
              <EyeOff size={12} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Supprimer définitivement l'encart "${blockId}" ?`)) run(onDelete);
              }}
              disabled={busy || !onDelete}
              data-testid={`block-${blockId}-delete`}
              title="Supprimer"
              className="w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 disabled:opacity-40 shadow-md"
            >
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
      {children}
    </Tag>
  );
}
